// src/pages/ProductAvatarImages.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Download,
  Trash2,
  X,
  ExternalLink,
  Video,
  AlertTriangle,
} from "lucide-react";
import GenerateProductAvatarImageModal from "../components/GenerateProductAvatarImageModal";
import { useAuth } from "@clerk/clerk-react";
import { useProductStore } from "../store/useProductStore";
import Loader from "../components/Loader";

const PAGE_SIZE = 12;
const FEATURE = "product-avatar-image";

export default function ProductAvatarImages() {
  const { getToken } = useAuth();
  const { selectedProduct } = useProductStore();
  const { projectId: paramProjectId } = useParams();
  const navigate = useNavigate();

  const projectId = selectedProduct?.id || paramProjectId;

  const [assets, setAssets] = useState([]);
  const [projectInputs, setProjectInputs] = useState([]);

  // ✅ infinite scroll (match ObjectRemover pattern)
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [hasMore, setHasMore] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [preview, setPreview] = useState(null); // { url, id }
  const [error, setError] = useState(null);
  const [queueNotice, setQueueNotice] = useState(false);

  const loaderRef = useRef(null);
  const previewBackdrop = useRef(null);
  const pollTimerRef = useRef(null);

  const [quota, setQuota] = useState({
    loading: true,
    unlimited: false,
    remaining: 0,
    used: 0,
    baseCap: 0,
    imagesExtra: 0,
  });

  const fetchQuota = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-avatar-images/quota`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuota({ loading: false, ...res.data });
    } catch {
      setQuota((q) => ({ ...q, loading: false }));
    }
  }, [getToken]);

  const featureAssets = useMemo(() => {
    return (assets || [])
      .filter((a) => (a?.metadata?.feature || "").toLowerCase() === FEATURE)
      // ✅ Hide soft-deleted items (delete should NOT affect quota, but should disappear from UI)
      .filter((a) => {
        const meta = a?.metadata || {};
        const st = String(meta.status || "").toLowerCase();
        const isDeleted = meta.deleted === true || st === "deleted";
        const isFailed = st === "failed";
        return !isDeleted && !isFailed;
      })
      .sort(
        (l, r) =>
          new Date(r.updated_at || r.created_at) -
          new Date(l.updated_at || l.created_at)
      );
  }, [assets]);

  // ✅ page-based slicing (match ObjectRemover)
  const pagedAssets = useMemo(
    () => featureAssets.slice(0, page * PAGE_SIZE),
    [featureAssets, page]
  );

  // ✅ match ObjectRemover: stable recalcHasMore(arr, pageNum)
  const recalcHasMore = useCallback((arr, pageNum) => {
    const p = Number(pageNum) || 1;
    setHasMore(p * PAGE_SIZE < (arr?.length || 0));
  }, []);

  const fetchProject = useCallback(
    async (silent = false) => {
      // ✅ If no analyzed project yet, don’t error — just show CTA and empty grid.
      if (!projectId) {
        setProjectInputs([]);
        setAssets((prev) =>
          (prev || []).filter((p) => String(p?.id || "").startsWith("tmp-"))
        ); // keep optimistic only (if any)
        setError(null);
        if (!silent) setInitialLoading(false);
        return;
      }

      if (!silent) {
        setInitialLoading(true);
        // ✅ reset pagination on full fetch
        setPage(1);
        pageRef.current = 1;
      }

      try {
        const token = await getToken();
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setAssets(Array.isArray(data.assets) ? data.assets : []);
        setProjectInputs([
          ...(Array.isArray(data.images) ? data.images : []),
          ...(Array.isArray(data.videos) ? data.videos : []),
        ]);
        setError(null);
      } catch {
        setError("Failed to fetch assets.");
      } finally {
        if (!silent) setInitialLoading(false);
      }
    },
    [getToken, projectId]
  );

  useEffect(() => {
    setInitialLoading(true);
    setPage(1);
    pageRef.current = 1;
    setHasMore(true);
    fetchProject(false);
    fetchQuota();
  }, [fetchProject, fetchQuota, projectId]);

  const hasPending = useMemo(() => {
    return featureAssets.some(
      (a) => String(a?.metadata?.status || "").toLowerCase() !== "finished"
    );
  }, [featureAssets]);

  useEffect(() => {
    setQueueNotice(hasPending);
  }, [hasPending]);

  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }

    if (hasPending) {
      pollTimerRef.current = setInterval(() => {
        fetchProject(true);
      }, 3000);
    } else {
      void fetchQuota();
    }

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [hasPending, fetchProject, fetchQuota]);

  // ✅ match ObjectRemover: keep pageRef in sync
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // ✅ match ObjectRemover: keep hasMore in sync when page/list changes
  useEffect(() => {
    setHasMore(page * PAGE_SIZE < featureAssets.length);
  }, [page, featureAssets.length]);

  // ✅ match ObjectRemover: loadMore increments page
  const loadMore = useCallback(() => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [loadingMore]);

  useEffect(() => {
    if (!hasMore || initialLoading || loadingMore) return;
    const el = loaderRef.current;
    if (!el) return;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "100px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, initialLoading, loadingMore, loadMore]);

  const addOptimisticPending = useCallback(() => {
    const tmpId = `tmp-${Date.now()}`;
    setAssets((prev) => [
      {
        id: tmpId,
        url: "/spinner.gif#pending",
        name: `product-avatar-${tmpId}`,
        type: "photo",
        metadata: { feature: FEATURE, status: "pending", local: true },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      ...prev,
    ]);

    // ✅ keep hasMore correct for current page (match ObjectRemover style)
    recalcHasMore(featureAssets, pageRef.current);
  }, [recalcHasMore, featureAssets]);

  const handleDownload = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDelete = async (assetId) => {
    if (!assetId) return;
    if (!window.confirm("Delete this image?")) return;

    try {
      const token = await getToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/product-avatar-images/${assetId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // ✅ Remove locally from UI, but quota should NOT change (backend soft-delete keeps row counted)
      setAssets((prev) => {
        const next = prev.filter((a) => a.id !== assetId);
        recalcHasMore(featureAssets, pageRef.current);
        return next;
      });

      if (preview?.id === assetId) setPreview(null);

      // Optional: keep quota in sync (should remain unchanged now)
      void fetchQuota();
    } catch {
      // eslint-disable-next-line no-alert
      alert("Delete failed");
    }
  };

  const handlePreview = (asset) => setPreview({ url: asset.url, id: asset.id });

  useEffect(() => {
    if (!preview) return;

    const onKey = (e) => e.key === "Escape" && setPreview(null);
    const onClick = (e) => {
      if (previewBackdrop.current && e.target === previewBackdrop.current) {
        setPreview(null);
      }
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [preview]);

  useEffect(() => {
    if (selectedProduct?.id && selectedProduct.id !== paramProjectId) {
      navigate(`/generate/product-avatar-image/${selectedProduct.id}`);
    }
  }, [selectedProduct, paramProjectId, navigate]);

  const goToAvatarVideos = () =>
    navigate(`/generate/product-avatar-video/${projectId}`);

  const canGenerate = quota.unlimited || quota.remaining > 0;
  const totalImages = quota.unlimited
    ? Infinity
    : (quota.baseCap || 0) + (quota.imagesExtra || 0);
  const remaining = quota.unlimited ? Infinity : quota.remaining || 0;
  const used = quota.unlimited ? 0 : quota.used || 0;

  const showBar =
    !quota.loading &&
    !quota.unlimited &&
    Number.isFinite(totalImages) &&
    totalImages > 0;

  const pct = showBar
    ? Math.min(100, Math.max(0, Math.round((used / totalImages) * 100)))
    : 0;

  const isInitialLoading = initialLoading && !error && featureAssets.length === 0;

  return (
    <div className="w-full px-2 sm:px-4 md:px-8 py-5 sm:py-8">
      <div className="max-w-[1600px] mx-auto bg-[#15152B] border border-[#2A2A40] rounded-3xl px-2 sm:px-4 md:px-8 py-6 sm:py-10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Product Avatar Images
          </h1>

          {!quota.loading && (
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 py-2 border ${
                quota.unlimited
                  ? "border-pink-600/60 bg-pink-600/10"
                  : "border-[#2A2A40] bg-[#1C1C34]"
              }`}
              title={
                quota.unlimited
                  ? "Unlimited images this cycle"
                  : "Images usage this cycle"
              }
            >
              {quota.unlimited ? (
                <span className="text-pink-400 font-semibold text-sm sm:text-base">
                  Unlimited images
                </span>
              ) : (
                <span className="text-white/80 text-sm sm:text-base">
                  <span className="font-semibold text-white">{remaining}</span>{" "}
                  left of{" "}
                  <span className="font-semibold text-white">{totalImages}</span>
                </span>
              )}
            </div>
          )}
        </div>

        {showBar && (
          <div className="mb-3">
            <div className="h-2 w-full bg-[#21213a] rounded-full overflow-hidden border border-[#2A2A40]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background:
                    "linear-gradient(90deg, rgba(252,39,131,0.9) 0%, rgba(252,39,131,0.7) 100%)",
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-white/60">
              <span>Used: {used}</span>
              <span>Total: {totalImages}</span>
            </div>
          </div>
        )}

        <p className="text-white/70 mb-4 sm:mb-6 text-sm sm:text-base">
          Generate photorealistic images of your product held by AI avatars.
        </p>

        {queueNotice ? (
          <div className="mb-4 rounded-2xl border border-amber-700/40 bg-amber-600/15 px-4 py-3 text-amber-200">
            <div className="font-semibold text-sm sm:text-base">
              Task queued successfully
            </div>
            <div className="text-xs sm:text-sm text-amber-100/90 mt-1">
              Your request has been queued successfully. It will start shortly, and you’ll receive a notification when it’s ready.
            </div>
          </div>
        ) : null}

        <div className="mb-4 sm:mb-6">
          <button
            onClick={goToAvatarVideos}
            className="flex items-center gap-2 rounded-xl px-6 py-2 font-semibold text-white bg-[#23233a] border border-[#353550] shadow hover:text-[#FC2783] hover:shadow-pink-500/40 transition-all duration-200 hover:bg-white hover:border-pink-500 focus:ring-2 focus:ring-pink-600 hover:scale-105"
            style={{
              boxShadow: "0 2px 16px 0 rgba(252,39,131,0.09)",
              minHeight: "44px",
              minWidth: "220px",
            }}
          >
            <Video size={20} />
            <span className="font-bold">Generate Product Avatar Video</span>
          </button>
        </div>

        {error && (
          <div className="mb-4 sm:mb-6 flex items-center gap-2 text-red-400">
            <AlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        {isInitialLoading ? (
          <div className="w-full py-16 flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:gap-6 grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
              <div
                onClick={() => {
                  // ✅ Always show CTA; if no project yet, route to Analyze instead of opening modal
                  if (!projectId) {
                    navigate("/analyze"); // adjust if your route differs
                    return;
                  }

                  if (!canGenerate) {
                    navigate("/pricing");
                    return;
                  }
                  setModalOpen(true);
                }}
                className={`flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A50] text-white rounded-lg min-h-[160px] h-[210px] sm:h-[300px] ${
                  !projectId || !canGenerate
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer hover:border-pink-500 hover:text-pink-500"
                } transition text-center p-4 sm:p-6`}
                title={
                  !projectId
                    ? "Analyze a product first to start generating"
                    : !canGenerate
                    ? "Out of images. Click to manage plan."
                    : "Pick an input and avatar to generate"
                }
              >
                <Plus className="w-7 h-7 sm:w-8 sm:h-8 mb-2" />
                <p className="font-semibold text-base">
                  Create Product Avatar Image
                </p>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  {!projectId
                    ? "Analyze your first product to get started"
                    : quota.unlimited
                    ? "Unlimited images"
                    : `You have ${remaining} left of ${totalImages} this cycle`}
                </p>

                {!!projectId && !canGenerate && (
                  <p className="text-xs text-red-300 mt-2">
                    You’ve reached your image limit. Upgrade or add images.
                  </p>
                )}
              </div>

              {pagedAssets.map((a) => {
                const status = String(a?.metadata?.status || "").toLowerCase();
                const isPending = status !== "finished";
                const url = isPending ? "/spinner.gif" : a.url;

                return (
                  <div
                    key={a.id}
                    className="relative group bg-gradient-to-br from-[#1C1C2E] to-[#252538] border border-[#2A2A40] text-white rounded-2xl p-2 sm:p-3 transition transform hover:scale-[1.03] hover:shadow-xl hover:ring-1 hover:ring-pink-500/30 min-h-[160px] h-[210px] sm:h-[300px] flex items-center justify-center overflow-hidden"
                  >
                    {isPending ? (
                      <>
                        <img
                          src="/spinner.gif"
                          alt="Generating..."
                          className="object-contain w-full mx-auto rounded-lg opacity-70 animate-pulse"
                          draggable={false}
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 32px)",
                          }}
                        />
                        {/* ✅ Match Upscaler: delete button only top-right for pending */}
                        <div className="absolute top-2 right-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(a.id);
                            }}
                            className="bg-black/80 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <img
                          src={url}
                          alt="Avatar Product"
                          className="object-contain max-h-[88%] w-full mx-auto rounded-lg transition-opacity duration-300 group-hover:opacity-70 cursor-pointer"
                          onClick={() => handlePreview(a)}
                          draggable={false}
                          style={{
                            background: "#111124",
                            height: "100%",
                            maxHeight: "calc(100% - 32px)",
                          }}
                        />

                        {/* ✅ Match Upscaler: delete lives in hover overlay (alongside download/open) */}
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none z-20"
                          style={{ background: "rgba(21, 21, 43, 0.15)" }}
                        >
                          <div className="flex gap-3 pointer-events-auto">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(url);
                              }}
                              className="bg-black/80 hover:bg-pink-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Download"
                            >
                              <Download size={20} />
                            </button>

                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="bg-black/80 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Open in new tab"
                            >
                              <ExternalLink size={20} />
                            </a>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(a.id);
                              }}
                              className="bg-black/80 hover:bg-red-600 text-white rounded-full p-3 shadow-lg transition"
                              title="Delete"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* ✅ match ObjectRemover: only show sentinel when there is more */}
            {hasMore && !initialLoading && (
              <div
                ref={loaderRef}
                className="w-full flex justify-center py-8 pointer-events-none"
              >
                {loadingMore && (
                  <img
                    src="/spinner.gif"
                    alt="Loading..."
                    className="w-7 h-7 mx-auto"
                  />
                )}
              </div>
            )}
          </>
        )}

        {modalOpen && (
          <GenerateProductAvatarImageModal
            projectId={projectId}
            projectInputs={projectInputs}
            onClose={() => setModalOpen(false)}
            refreshImages={() => {
              addOptimisticPending();
            
              if (!quota.unlimited) {
                setQuota((q) => ({
                  ...q,
                  remaining: Math.max(0, (q.remaining || 0) - 1),
                  used: (q.used || 0) + 1,
                }));
              }
            
              setQueueNotice(true);
              fetchProject(true);
            }}
          />
        )}

        {preview && (
          <div
            ref={previewBackdrop}
            className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center px-2 select-none"
            style={{ animation: "fadeIn 0.18s" }}
          >
            <div className="relative bg-[#22223a] rounded-2xl max-w-[98vw] shadow-2xl p-0 flex flex-col">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-2 right-2 bg-white/90 hover:bg-pink-600 text-black hover:text-white rounded-full p-2 shadow transition"
                title="Close"
              >
                <X size={21} />
              </button>

              <div className="p-6 flex flex-col items-center justify-center">
                <img
                  src={preview.url}
                  alt="Preview"
                  className="max-w-[95vw] max-h-[78vh] object-contain rounded-xl bg-black"
                  style={{ boxShadow: "0 4px 32px 0 rgba(30,25,60,0.5)" }}
                  draggable={false}
                />
              </div>

              <div className="flex justify-center gap-4 pb-5">
                <button
                  type="button"
                  onClick={() => handleDownload(preview.url)}
                  className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Download
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(preview.id)}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Delete
                </button>
                <a
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-2 font-semibold shadow transition"
                >
                  Open in new tab
                </a>
              </div>

              {error && (
                <div className="text-red-500 mt-2 px-6 pb-3">{error}</div>
              )}
            </div>
          </div>
        )}

        {error && !initialLoading && (
          <div className="text-red-500 mt-4">{error}</div>
        )}
      </div>
    </div>
  );
}
